<template>
  <div
    class="w-full h-12 flex items-center justify-between shrink-0 p-3 border-b border-secondary"
    :class="{
      'ltr:pl-4 rtl:pr-4': viewport !== 'desktop',
      'ltr:pl-5 rtl:pr-5 bg-gradient-gray-neural': viewport === 'desktop',
      'ltr:lg:pl-5 rtl:lg:pr-5 lg:bg-gradient-gray-neural':
        viewport === 'reactive',
    }"
  >
    <div class="w-full flex items-center justify-between grow overflow-hidden">
      <div class="w-full flex items-center gap-2">
        <Indicator
          :variant="variant"
          size="3xs"
          mode="solid"
          :imageUrl="imageUrl"
        />
        <span class="text-primary text-style-base font-medium truncate">
          <slot>
            {{ text }}
          </slot>
        </span>
      </div>
      <MessengerFlowModal
        v-if="itemType === 'spaces'"
        :open="openMessengerFlowModal"
        @update:open="openMessengerFlowModal = $event"
        :space="item"
      >
        <Tooltip
          :title="$t('tooltips.buttons.messenger_flow_url')"
          side="bottom"
        >
          <IconButton
            variant="tertiary"
            type="gray"
            :size="isMobile ? 'lg' : 'xs'"
          >
            <Icon name="chat-happy" />
          </IconButton>
        </Tooltip>
      </MessengerFlowModal>
      <div v-if="!type?.includes('detail')" class="flex items-center gap-0.5">
        <DeleteModal
          v-if="canDelete"
          :title="deleteTitle"
          :description="deleteDescription"
          :hints="deleteHints"
          @delete="$emit('delete')"
        >
          <Tooltip :title="$t('tooltips.buttons.delete')" side="bottom">
            <IconButton
              variant="tertiary"
              type="gray"
              :size="isMobile ? 'lg' : 'xs'"
            >
              <Icon name="delete" />
            </IconButton>
          </Tooltip>
        </DeleteModal>
        <DialogClose
          v-show="isMobile"
          ref="close"
          :as="CloseButton"
          size="lg"
          :aria-label="$t('components.buttons.close')"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from "vue";
import { CloseButton, IconButton } from "@/SharedComponents/Buttons";
import { DialogClose } from "@/SharedComponents/Dialog";
import { Icon } from "@/SharedComponents/Icon";
import { Indicator } from "@/SharedComponents/Indicator";
import { DeleteModal, MessengerFlowModal } from "@/SharedComponents/Modals";
import { Tooltip } from "@/SharedComponents/Tooltips";
import { useBreakpoints } from "@/Composables/useBreakpoints";
import { useI18n } from "vue-i18n";

defineEmits(["delete"]);

const close = ref(null);
const openMessengerFlowModal = ref(false);

const props = defineProps({
  type: { type: String, required: true },
  text: { type: String, required: false },
  space: { type: Object, required: false },
  imageUrl: { type: String, required: false },
  canDelete: { type: Boolean, required: false },
  item: { type: Object, required: false },
  itemType: { type: String, required: false },
  viewport: {
    type: String,
    validator: (val) => ["mobile", "desktop", "reactive"].includes(val),
    default: "reactive",
  },
});

const variant = computed(() => {
  switch (props.type) {
    case "assets":
      return "asset";
    case "asset-groups":
      return "asset_group";
    case "spaces":
      return "space";
    case "building":
      return "building";
    case "organisation":
    default:
      return "roles";
  }
});

const { t } = useI18n();

const deleteTitle = computed(() => {
  switch (props.itemType) {
    case "assets":
      return t("components.modals.delete.asset.title");
    case "spaces":
      return t("components.modals.delete.space.title");
    default:
      return t("components.modals.delete.default.title");
  }
});

const deleteDescription = computed(() => {
  switch (props.itemType) {
    case "assets":
      return t("components.modals.delete.asset.description");
    case "spaces":
      return t("components.modals.delete.space.description");
    default:
      return t("components.modals.delete.default.description");
  }
});

const deleteHints = computed(() => {
  switch (props.itemType) {
    case "assets":
      return t("components.modals.delete.asset.hints").split("\\n");
    case "spaces":
      return t("components.modals.delete.space.hints").split("\\n");
    default:
      return undefined;
  }
});

const { maxTablet } = useBreakpoints();

const isMobile = computed(() => {
  switch (props.viewport) {
    case "mobile":
      return true;
    case "desktop":
      return false;
    case "reactive":
    default:
      return maxTablet.value;
  }
});

defineExpose({
  close: () => close.value?.$el.click(),
});
</script>
